import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Mail, Lock, CheckCircle, AlertCircle } from 'lucide-react';
import { Logo } from '../components/Logo';
import api from '../services/api';

// WhatsApp-first registration landing page (2026-10-07, Jaime-approved).
// The user arrives from a single-use WhatsApp link (24h, bound to their
// number), sets name/email/password, and must then click the email
// verification link before login works.
type Phase = 'checking' | 'form' | 'done' | 'error';

export default function CompletarRegistro() {
  const [phase, setPhase] = useState<Phase>('checking');
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const token = new URLSearchParams(window.location.search).get('token') || '';

  useEffect(() => {
    if (!token || token.length < 32) {
      setError('Enlace inválido. Envía un mensaje por WhatsApp para recibir uno nuevo.');
      setCode('INVALID_TOKEN');
      setPhase('error');
      return;
    }
    api.get('/whatsapp-registration/status', { params: { token } })
      .then((res) => {
        if (res.data?.data?.valid) setPhase('form');
        else {
          setCode(res.data?.code || 'TOKEN_NOT_FOUND');
          setError('Este enlace ya fue utilizado o expiró. Envía un nuevo mensaje por WhatsApp para recibir otro.');
          setPhase('error');
        }
      })
      .catch((e) => {
        setCode(e.response?.data?.code || 'TOKEN_NOT_FOUND');
        setError(e.response?.data?.error || 'Este enlace ya fue utilizado o expiró. Envía un nuevo mensaje por WhatsApp para recibir otro.');
        setPhase('error');
      });
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 3 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || password.length < 8) {
      setError('Completa todos los campos (nombre, correo válido y contraseña de al menos 8 caracteres).');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await api.post('/whatsapp-registration/complete', { token, name: name.trim(), email: email.trim().toLowerCase(), password });
      setPhase('done');
    } catch (err: any) {
      setError(err.response?.data?.error || 'No pudimos completar tu registro. Intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B2D3C] to-[#123F52] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8">
        <div className="flex justify-center mb-6"><Logo /></div>

        {phase === 'checking' && (
          <p className="text-center text-gray-500">Validando tu enlace…</p>
        )}

        {phase === 'error' && (
          <div className="text-center space-y-4" role="alert">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
            <p className="text-gray-700">{error}</p>
            {code === 'TOKEN_EXPIRED' && (
              <a
                href="https://wa.me/15717126394?text=Quiero%20crear%20mi%20cuenta%20Vecindus"
                target="_blank"
                rel="noreferrer"
                className="inline-block bg-[#25D366] text-white font-semibold px-6 py-3 rounded-xl"
              >
                Pedir nuevo enlace por WhatsApp
              </a>
            )}
            <p><Link to="/login" className="text-[#36CCD8] font-medium">Ir a iniciar sesión</Link></p>
          </div>
        )}

        {phase === 'form' && (
          <form onSubmit={submit} className="space-y-4">
            <h1 className="text-xl font-bold text-gray-900 text-center">Completa tu cuenta Vecindus</h1>
            {error && <p role="alert" className="text-red-500 text-sm">{error}</p>}
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre completo"
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 outline-none"
              />
            </div>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Correo electrónico"
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 outline-none"
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Contraseña (mínimo 8 caracteres)"
                className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#36CCD8] focus:ring-2 focus:ring-[#36CCD8]/20 outline-none"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#36CCD8] hover:bg-[#2FB6C1] disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-colors"
            >
              {submitting ? 'Creando tu cuenta…' : 'Crear mi cuenta'}
            </button>
            <p className="text-xs text-gray-500 text-center">
              Te enviaremos un correo para verificar tu cuenta antes de poder entrar.
            </p>
          </form>
        )}

        {phase === 'done' && (
          <div className="text-center space-y-4">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
            <h1 className="text-xl font-bold text-gray-900">¡Casi listo!</h1>
            <p className="text-gray-700">
              Enviamos un correo de verificación a <strong>{email}</strong>. Haz clic en el enlace
              para activar tu cuenta; después podrás iniciar sesión normalmente.
            </p>
            <p><Link to="/login" className="text-[#36CCD8] font-medium">Ir a iniciar sesión</Link></p>
          </div>
        )}
      </div>
    </div>
  );
}
